import { useLayoutEffect, useMemo, useRef } from "react";
import { useTranslations as useIntlTranslations } from "use-intl";

type Translator = ReturnType<typeof useIntlTranslations>;
type Latest = { t: Translator; version: number };

let versions = 0;

const METHODS = ["rich", "markup", "raw", "has"] as const;

/** A translator that answers with `latest` once a newer one has been committed. */
const liveTranslator = (
	own: Translator,
	version: number,
	latest: { current: Latest },
): Translator => {
	const pick = (): Translator =>
		latest.current.version > version ? latest.current.t : own;
	const live = ((...args: unknown[]) =>
		(pick() as (...a: unknown[]) => unknown)(...args)) as Translator;
	for (const method of METHODS) {
		(live as unknown as Record<string, unknown>)[method] = (
			...args: unknown[]
		) =>
			(pick()[method] as unknown as (...a: unknown[]) => unknown).apply(
				pick(),
				args,
			);
	}
	return live;
};

/**
 * use-intl's useTranslations, except that the function it returns speaks the
 * language that is on screen *when it is called*.
 *
 * Fetch callbacks and timers created on mount keep the translator of the
 * render they were created in. A viewer whose saved language is not Thai
 * hydrates in Thai and switches a moment later, so without this a request
 * that fails after the switch would still toast in Thai. The identity still
 * changes with the language, so effects that list `t` re-run as before.
 */
export const useTranslations = ((namespace?: string) => {
	const t = useIntlTranslations(namespace as never) as Translator;
	const latest = useRef<Latest>({ t, version: 0 });
	const { live, version } = useMemo(() => {
		const v = ++versions;
		return { live: liveTranslator(t, v, latest), version: v };
	}, [t]);
	useLayoutEffect(() => {
		latest.current = { t, version };
	}, [t, version]);
	return live;
}) as typeof useIntlTranslations;
